import { ChevronLeft, ChevronRight } from 'lucide-react'

import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import Text from '@/elements/Text'

import './Pagination.scss'
import { useEffect, useMemo, useState } from 'react'
import type { PaginationParamsType } from '@/api/request'

type PaginationProps = {
  onPageChange: (page: number) => void
  variant?: 'default' | 'compact'
} & Partial<PaginationParamsType>

function Pagination({
  page = 1,
  totalPages,
  onPageChange,
  variant = 'default',
}: PaginationProps) {
  const [internalTotalPages, setInternalTotalPages] = useState(totalPages || 0)
  const isCompact = variant === 'compact'

  const startPage = useMemo(() => {
    if (internalTotalPages <= 5) return 1
    let start = Math.max(1, page - 2)
    if (start + 4 > internalTotalPages) {
      start = Math.max(1, internalTotalPages - 4)
    }
    return start
  }, [page, internalTotalPages])

  const endPage = useMemo(() => {
    if (internalTotalPages <= 5) return internalTotalPages
    return Math.min(internalTotalPages, startPage + 4)
  }, [internalTotalPages, startPage])

  const pagesToShow = useMemo(() => {
    const tempPages = []
    for (let p = startPage; p <= endPage; p++) {
      tempPages.push(p)
    }
    return tempPages
  }, [startPage, endPage])

  useEffect(() => {
    if (!totalPages) return
    setInternalTotalPages(totalPages)
  }, [totalPages])

  if ((totalPages || 0) < 2) return null

  return (
    <Flex className='pagination' inline align='center' gap={2}>
      <Button
        variant='iconBtn'
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1}
        styles={{ border: isCompact ? '' : 'none' }}
      >
        <ChevronLeft />
      </Button>
      {!isCompact && (
        <Flex
          inline
          align='center'
          color='white'
          className='pagination-pages'
          radius='small'
          p={2}
          gap={2}
        >
          {startPage > 1 && (
            <>
              <button
                key={1}
                onClick={() => onPageChange(1)}
                className={`${page === 1 ? 'active' : ''}`}
              >
                <Text
                  variant='footnote'
                  color={page === 1 ? 'black' : 'black-light-40'}
                >
                  1
                </Text>
              </button>
              {startPage > 2 && (
                <Text variant='footnote' color='black-light-40'>
                  ...
                </Text>
              )}
            </>
          )}
          {pagesToShow.map(p => (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`${page === p ? 'active' : ''}`}
            >
              <Text
                variant='footnote'
                color={page === p ? 'black' : 'black-light-40'}
              >
                {p}
              </Text>
            </button>
          ))}
          {internalTotalPages > endPage && endPage < internalTotalPages && (
            <>
              <Text variant='footnote' color='black-light-40'>
                ...
              </Text>
              <button
                key={internalTotalPages}
                onClick={() => onPageChange(internalTotalPages)}
                className={`${page === internalTotalPages ? 'active' : ''}`}
              >
                <Text
                  variant='footnote'
                  color={page === internalTotalPages ? 'black' : 'black-light-40'}
                >
                  {internalTotalPages}
                </Text>
              </button>
            </>
          )}
        </Flex>
      )}
      <Button
        variant='iconBtn'
        onClick={() => onPageChange(page + 1)}
        disabled={page === internalTotalPages}
        styles={{ border: isCompact ? '' : 'none' }}
      >
        <ChevronRight />
      </Button>
    </Flex>
  )
}

export default Pagination
